import { useState, useRef, useEffect } from "react"
import { Scrollbar } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import "swiper/css/bundle"
import Arrow from "@/common/icons/arrow"
import Button from "@/common/button/button"
import styles from "./slider.module.css"
import Block from "@/components/wrappers/block"
import classNames from "classnames"

export default function Slider({
  id,
  heading,
  text,
  children,
  button,
  theme,
  accentColor
}) {
  const containerRef = useRef(null)
  const [mounted, setMounted] = useState(false)
  const [offset, setOffset] = useState(null)
  const [mySwiper, setMySwiper] = useState(null)

  useEffect(() => {
    if (!children || children.length === 0) {
      return null
    }
    let timeoutId = null

    const resizeListener = () => {
      clearTimeout(timeoutId)

      timeoutId = setTimeout(() => {
        setOffset(containerRef.current.offsetLeft)
      }, 150)
    }

    window.addEventListener("resize", resizeListener)
    setOffset(containerRef.current.offsetLeft)

    if (mySwiper) {
      if (mySwiper.params) {
        mySwiper.params.slidesOffsetBefore = offset
        mySwiper.params.slidesOffsetAfter = offset
        mySwiper.update()
      }
    }

    return () => {
      // remove resize listener
      window.removeEventListener("resize", resizeListener)
    }
  }, [mySwiper, offset, children])

  useEffect(() => setMounted(true), [])

  if (!children || children.length === 0) {
    return null
  }

  const settings = {
    onSwiper: (swiper) => {
      setMySwiper(swiper)
    },
    scrollbar: {
      el: ".swiper-scrollbar-" + id,
      draggable: true,
      dragSize: 200
    },
    slidesPerView: "auto",
    spaceBetween: 20,
    breakpoints: {
      768: {
        spaceBetween: 24
      },
      1024: {
        spaceBetween: 32
      },
      1280: {
        spaceBetween: 40
      }
    }
  }

  return (
    <>
      <Block
        block={{ name: "slider-heading" }}
        width="full"
        gap="none"
        className={classNames(styles["bg" + theme], styles.heading)}>
        <h2 ref={containerRef}>{heading}</h2>
        {text && <p dangerouslySetInnerHTML={{ __html: text }}></p>}
      </Block>
      {mounted && (
        <Swiper
          {...settings}
          modules={[Scrollbar]}
          id={id}
          className={styles.slider}>
          {children.map((child, index) => {
            return (
              <SwiperSlide className={styles.slide} key={`s-${index}`}>
                {child}
              </SwiperSlide>
            )
          })}
        </Swiper>
      )}
      <Block
        block={{ name: "slider-controllers" }}
        className={classNames(styles.controllers, styles.slider)}
        width="full"
        gap="none">
        <div className="flex">
          {button.url && (
            <Button
              tagName="a"
              ariaLabel={button.label}
              target={button.target}
              href={button.url}
              className={styles.cta}
              theme={button.theme}>
              {button.label}
            </Button>
          )}
        </div>
        <div className={styles.scrollbarWrapper}>
          <div className={styles.scrollbar}>
            <div
              className={classNames(
                styles.swiperScrollbar,
                "swiper-scrollbar-" + id,
                accentColor
              )}></div>
          </div>
        </div>
        <div className={styles.navigation}>
          <button
            aria-label="Föregående"
            onClick={() => {
              if (!mySwiper) return
              const group = Math.max(
                1,
                Math.floor(
                  mySwiper.width / (320 + mySwiper.params.spaceBetween)
                )
              )
              mySwiper.slideTo(mySwiper.activeIndex - group)
            }}
            className={classNames(styles.button, "prev")}>
            <Arrow />
          </button>
          <button
            aria-label="Nästa"
            onClick={() => {
              if (!mySwiper) return
              const group = Math.max(
                1,
                Math.floor(
                  mySwiper.width / (320 + mySwiper.params.spaceBetween)
                )
              )
              mySwiper.slideTo(mySwiper.activeIndex + group)
            }}
            className={classNames(styles.button, "next")}>
            <Arrow />
          </button>
        </div>
      </Block>
    </>
  )
}
